import { useGoogleLogin } from '@react-oauth/google';
import { useState, useCallback, useEffect } from 'react';
import getCMResponse from '../../../utils/api_manager';
import styles from './GoogleMerchantCenterAuth.module.css';
import GoogleMerchantCenterIcon from '../../../assets/icons/google-merchant-center.svg';
import SingleSelectorDialogBox from '../../Selectors/SingleSelectorDialogBox/SingleSelectorDialogBox';

const GMC_SCOPE = 'https://www.googleapis.com/auth/content';

const GoogleMerchantCenterAuth = ({
    grantedScopes = [],
    requiredScopes = [],
    onGrantedScopesUpdate,
    project,
    providerProjectId,
    onLoginReady,
    // Grant-access loader centralization (requirement 3): raised across the
    // save-access-tokens -> list-merchant-accounts span and the final
    // save-datasource call. Optional -- only GrantOnboardingAccess supplies
    // it today.
    onLoadingChange,
}) => {
    const [showAccountSelector, setShowAccountSelector] = useState(false);
    const [merchantAccounts, setMerchantAccounts] = useState([]);
    const [error, setError] = useState(null);

    const isCompleted = providerProjectId != null;

    const handleAccountSelect = useCallback((account) => {
        onLoadingChange?.(true);
        getCMResponse('/datasource/google-merchant-center/save-datasource', 'POST', {
            project_id: project.id,
            merchant_id: account.id,
        }).then(() => {
            setShowAccountSelector(false);
            onGrantedScopesUpdate();
        }).catch(err => {
            console.error('Error saving Google Merchant Center datasource:', err);
            setError('Failed to save merchant account. Please try again.');
        }).finally(() => {
            onLoadingChange?.(false);
        });
    }, [project, onGrantedScopesUpdate, onLoadingChange]);

    const login = useGoogleLogin({
        onSuccess: (code) => {
            const gtkn = code['code'];
            if (!gtkn) return;
            setError(null);
            onLoadingChange?.(true);

            getCMResponse('/datasource/google-merchant-center/save-access-tokens', 'POST', {
                gcode: gtkn,
                project_id: project.id,
            }).then(() => {
                return getCMResponse('/datasource/google-merchant-center/list-merchant-accounts', 'GET', {
                    project_id: project.id,
                });
            }).then(result => {
                const accounts = (result?.data?.accounts || []).map(account => ({
                    id: account.name,
                    displayName: account.accountName || account.name,
                }));
                setMerchantAccounts(accounts);
                setShowAccountSelector(true);
            }).catch(err => {
                console.error('Error during Google Merchant Center auth:', err);
                setError('Authentication failed. Please try again.');
            }).finally(() => {
                onLoadingChange?.(false);
            });
        },
        flow: 'auth-code',
        scope: GMC_SCOPE,
        // TRD: oauth-credential-product-granularity/trd.md, Section 0.2/3.2 —
        // lets a second Google product connect (e.g. this one, after Ads or
        // Analytics is already connected) request only its own incremental
        // scope while still receiving back the union of every previously
        // granted scope, instead of Google silently narrowing the token to
        // just this request's scope. Confirmed (@react-oauth/google 0.12.2
        // dist/index.d.ts's CodeClientConfig) this is a first-class typed
        // option that the hook spreads straight into GIS's initCodeClient,
        // not an undocumented pass-through.
        include_granted_scopes: true,
        onError: (err) => console.error('Google Merchant Center OAuth error:', err),
    });

    // Expose login to parent so it can be called directly from a click handler
    // (user gesture context), avoiding popup blocking.
    useEffect(() => {
        onLoginReady?.(login);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // In managed mode the parent owns the trigger — render nothing until
    // the account selector is needed.
    if (onLoginReady && !showAccountSelector) return null;

    if (isCompleted) {
        return (
            <div className={styles.container}>
                <img
                    src={GoogleMerchantCenterIcon}
                    alt="Google Merchant Center - Connected"
                    className={`${styles.iconButton} ${styles.completed}`}
                />
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {!showAccountSelector ? (
                <img
                    src={GoogleMerchantCenterIcon}
                    alt="Connect Google Merchant Center"
                    onClick={() => { setError(null); login(); }}
                    className={styles.iconButton}
                />
            ) : (
                <SingleSelectorDialogBox
                    listOptions={merchantAccounts}
                    handleOptionSelect={handleAccountSelect}
                    setShowSelector={setShowAccountSelector}
                    primaryText="Select a Merchant Center account to connect"
                    ctaText="Connect"
                    optionIcon={GoogleMerchantCenterIcon}
                    dialogTitle="Select Merchant Center Account"
                />
            )}
            {error && <p className={styles.error}>{error}</p>}
        </div>
    );
};

export default GoogleMerchantCenterAuth;
